import React, {useCallback, useEffect, useRef} from 'react';
import {escapeRegularExpression} from 'webapp-lib/utils';

import {typedObserver} from '_/utilities/typed-observer';

import {Item as ItemType, NavigationMode} from '../actions';

import {StyledIcon, StyledListItem, TextContainer, TextItem} from './styles';

/** @deprecated Use EList instead */
export interface Props<
  TItemType extends ItemType<TItemType> = ItemType<object>,
  TElementType extends keyof HTMLElementTagNameMap = 'div',
  V extends keyof HTMLElementTagNameMap = 'div',
> extends Omit<
    React.HTMLAttributes<HTMLElementTagNameMap[TElementType]>,
    'onSelect' | 'onDrop'
  > {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  activated?: boolean;
  isDraggingOver?: boolean;
  baseElement?: TElementType;
  children?: React.ReactNode;
  highlight?: string;
  hoverSelectable?: boolean;
  index?: number;
  /**
   * Prevents the selection of this item by avoiding selection in the table or low-level list component
   */
  item: TItemType;
  ref?: React.Ref<HTMLElementTagNameMap[TElementType]>;
  sectioned?: boolean;
  selectable?: boolean;
  selectionBaseElement?: V;
  showSelectionCheckbox?: boolean;
  onActivate?: (item: TItemType) => void;
  onNavigate?: (item: TItemType, mode: NavigationMode) => void;
}

function highlightWord(
  source: string,
  target: string,
  callback: (text: string, key: string | number) => React.ReactElement = (
    text: string,
    key: string | number
  ): React.ReactElement => <strong key={key}>{text}</strong>
): (React.ReactElement | string)[] | string {
  const result: (React.ReactElement | string)[] = [];

  if (!source) {
    return result;
  }
  if (!target) {
    return source;
  }

  const regex = new RegExp(
    target
      .split(/\s/u)
      .filter((item) => item)
      .map(escapeRegularExpression)
      .join('|'),
    'giu'
  );

  let lastOffset = 0;

  source.replace(regex, (value: string, offset: number): string => {
    result.push(
      source.substr(lastOffset, offset - lastOffset),
      // Replace the string with JSX or anything.
      callback(value, offset)
    );
    lastOffset = offset + value.length;

    return '';
  });

  // Push the last non-highlighted string
  result.push(source.substr(lastOffset));

  return result;
}

/** @deprecated Use EList instead */
export function ListItemComponent<
  TItemType extends ItemType<TItemType> = ItemType<object>,
  TElementType extends keyof HTMLElementTagNameMap = 'div',
  V extends keyof HTMLElementTagNameMap = 'div',
>({
  baseElement,
  Icon,
  activated = false,
  className,
  children,
  item,
  highlight,
  hoverSelectable = false,
  selectable = false,
  selectionBaseElement,
  onActivate,
  onBlur,
  onKeyDown,
  onKeyPress,
  onMouseDown,
  onMouseOut,
  onMouseOver,
  onMouseUp,
  onNavigate,
  isDraggingOver,
  ref,
  // currently unused but required here, otherwise it would end up in the DOM
  index: _,
  ...props
}: Props<TItemType, TElementType, V>): React.ReactElement {
  type BaseHTMLElement = HTMLElementTagNameMap[TElementType];

  const internalRef = useRef<BaseHTMLElement>(null);
  const shownAt = useRef(new Date().getTime());

  const handleMouseUp = useCallback(
    (event: any): void => {
      if (item.inactive) {
        event.stopPropagation();
        event.preventDefault();

        return;
      }

      if (
        event.button === 0 &&
        (new Date().getTime() - shownAt.current > 200 ||
          process.env.NODE_ENV === 'test')
      ) {
        onActivate?.(item);
        event.stopPropagation();
      } else if (event.button === 2 && !activated) {
        onActivate?.(item);
      }
      onMouseUp?.(event);
    },
    [activated, onActivate, item, onMouseUp]
  );

  const internalRefCallback = useCallback(
    (element: BaseHTMLElement) => {
      internalRef.current = element;

      /* istanbul ignore if */
      if (ref) {
        if (typeof ref === 'function') {
          /* istanbul ignore next */
          ref(internalRef.current);
        } else {
          ref.current = internalRef.current;
        }
      }
    },
    [ref]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<BaseHTMLElement>): void => {
      if (event.key === 'Enter') {
        onActivate?.(item);
      }
      onKeyPress?.(event);
    },
    [onActivate, item, onKeyPress]
  );

  const handleMouseOver = useCallback(
    (event: React.MouseEvent<BaseHTMLElement>): void => {
      if (hoverSelectable) {
        onNavigate?.(item, NavigationMode.Normal);
      }
      onMouseOver?.(event);
    },
    [hoverSelectable, item, onNavigate, onMouseOver]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<BaseHTMLElement>): void => {
      if (selectable && event.button === 0) {
        onNavigate?.(item, NavigationMode.Normal);
      }
      onMouseDown?.(event);
    },
    [item, selectable, onNavigate, onMouseDown]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<BaseHTMLElement>): void => {
      onBlur?.(event);
      if (
        activated &&
        event.relatedTarget instanceof Node &&
        event.relatedTarget.contains(internalRef.current) &&
        event.relatedTarget !== internalRef.current
      ) {
        internalRef.current?.focus();
      }
    },
    [activated, onBlur]
  );

  useEffect(() => {
    if (activated && document.activeElement?.contains(internalRef.current)) {
      internalRef.current?.focus();
    }
  }, [activated]);

  const IconComponent = item.Icon || Icon;

  const {inactive = false} = item as ItemType<TItemType>;

  const element = baseElement || ('div' as TElementType);

  return React.createElement(
    StyledListItem as React.ElementType,
    {
      'aria-disabled': inactive,
      'aria-label':
        props.title || (typeof children === 'string' ? children : ''),
      'aria-selected': activated,
      activated,
      as: element,
      className,
      isDraggingOver,
      item,
      onMouseLeave: inactive ? undefined : handleBlur,
      onKeyDown: inactive ? undefined : onKeyDown,
      onKeyPress: inactive ? undefined : handleKeyPress,
      onMouseDown: inactive ? undefined : handleMouseDown,
      onMouseOut: inactive ? undefined : onMouseOut,
      onMouseOver: inactive ? undefined : handleMouseOver,
      onMouseUp: handleMouseUp,
      onTouchEnd: inactive ? undefined : handleMouseUp,
      ref: internalRefCallback,
      role: inactive ? '' : 'option',
      tabIndex: !inactive && activated ? 0 : -1,
      ...props,
    },
    IconComponent && (
      <StyledIcon baseElement={selectionBaseElement}>
        <IconComponent />
      </StyledIcon>
    ),
    typeof children === 'string' ? (
      <TextContainer>
        <TextItem>
          {highlight ? highlightWord(children, highlight) : children}
        </TextItem>
      </TextContainer>
    ) : (
      children
    )
  );
}

/** @deprecated Use EList instead */
export default typedObserver(ListItemComponent);
